// Timestop BO — Vite entry point.
//
// Bridge between the modular src/ tree and the PHP page
// (admin/index.php), which still contains the original inline <script>
// blocks during the migration window.
//
//   Phase 1:  load this file, prove the bundle wires up.
//   Phase 2:  publish utilities from src/utils/ as window.* so the inline
//             scripts use the bundle's versions.
//   Phase 3:  publish a thin apiFetch wrapper that delegates to the
//             bundle's apiFetch() while keeping the legacy "show login
//             modal on 401" side effect in index.php.
//   Phase 9:  delete the inline <script> blocks; the bundle becomes the
//             single source of truth.

import {
  escapeHtml,
  splitCurrency,
  buildCurrencyLookup,
  getCurrencyByISO,
  formatCurrency,
  formatCurrencyByISO,
  getTotalByCurrency,
  formatTs,
  formatDuration,
  driftSrv2Local,
  lastSeenFor,
  displayTerminalName,
  displayTerminalLocation,
  displayTerminalID,
  displayTerminalIcon,
  quickStatusIcons,
  logKey,
  terminalLogRow,
} from './utils';

import { apiFetch, type ApiFetchOptions, type ApiFetchConfig } from './api';

import {
  doLogin,
  doLogout,
  showLoginModal,
  hideLoginModal,
  isOperator,
  applyRoleGating,
  ensureLoginErrorPlacement,
  clearLoginErrors,
} from './auth';

import {
  renderKPIs,
  renderQuickTerminals,
  renderAllTerminals,
  renderGlobalLogs,
  renderTerminalLogs,
  renderGroups,
  renderUsers,
  renderLoginAttempts,
  renderNotify,
  installNotifyClickHandler,
  removeNotifyRow,
  renderAll,
  groupTerminalsByOid,
  // Group rename — same orange-flash + prompt() pattern as
  // editTerminalField (terminal-modal.ts). Surfaced from the
  // renderGroups card title; calls /api/group_update.
  editGroupName,
} from './render';

import { fetchData as fetchDataBundle, fetchTerminal as fetchTerminalBundle, fetchGroups as fetchGroupsBundle, fetchUsers as fetchUsersBundle, fetchLoginAttempts as fetchLoginAttemptsBundle, fetchLogs as fetchLogsBundle, loadCurrencyDefaults as loadCurrencyDefaultsBundle, fetchNotify as fetchNotifyBundle, markNotifyRead as markNotifyReadBundle } from './data';

import { attachState, createInitialState, type TimestopState } from './state';

// Phase 9: terminal modal, accounting, navigation, IP helpers
import {
  openTerminalImpl,
  closeModal,
  switchModalTab,
  saveSettings,
  editTerminalField,
  showConfirm,
  setPendingSetting,
  setPendingPrice,
  setPendingBetValue,
  setRuntime,
  toggleStatus,
  startHopperRefresh,
  stopHopperRefresh,
  updateHopperLevelsDisplay,
  prepareTerminal,
  renderCurrencyTotals,
  hasOwn,
  renderTab,
  renderTabOverview,
  renderTabLogs,
  renderTabSettings,
  renderTabCommands,
  renderTabAccounting,
  moveTerminal,
} from './terminal-modal';

import {
  normaliseAccounting,
  pickByIndex,
  renderAccounting,
  renderAccTable,
} from './accounting';

import {
  renderAccountingRanged,
  clearRangedState,
} from './accounting-ranged';

// Phase 12.12: Accounting page (group-level mirror of the
// per-terminal Ranged sub-tab). clearGroupAccountingState wipes the
// per-visit cache + range so re-entering the page (e.g. after a
// navigate) starts fresh.
import {
  renderAccountingGroup,
  clearGroupAccountingState,
} from './accounting-group';

import {
  navigate,
  toggleMobileMenu,
} from './navigation';

import {
  resolveAndShowIp,
  expandIpDetails,
  clearIpGeoCache,
} from './ip-helpers';

import { initTailwind } from './utils/tailwind';

import { showSpinner, hideSpinner, setSpinnerMessage, isSpinnerVisible } from './spinner';

// Phase 12.18: i18n (client-side t() helper) + locale catalog loader.
import {
  loadLocales,
  setActiveLang,
  onLangChange,
  t as tBundle,
  activeLang as activeLangBundle,
  type Lang as LangBundle,
} from './i18n';

// Phase 12.10: user-badge dropdown + Profile / Settings modals
import {
  toggleUserMenu,
  openUserMenu,
  closeUserMenu,
  handleUserMenuClick,
  openProfileModal,
  closeProfileModal,
  submitChangeMyPassword,
  openSettingsModal,
  closeSettingsModal,
  setLanguage,
} from './user-menu';

// Phase 12.13: avatar picker + cropper + badge painter
import {
  openAvatarPicker,
  closeAvatarCropper,
  confirmAvatarCrop,
  paintUserBadge,
} from './avatar';

// Phase 14: Web Push (subscribe / unsubscribe / status).
import {
  ensurePushReady,
  subscribeToPush,
  unsubscribeFromPush,
  logoutUnsubscribePush,
  attachPushState,
} from './push';

import {
  addGroup as addGroupModal,
  closeAddGroupModal,
  submitAddGroup as submitAddGroupModal,
  addUser as addUserModal,
  closeAddUserModal,
  toggleAddUserGroupField,
  submitAddUser as submitAddUserModal,
  showChangePasswordModal,
  changeUserPassword,
  showDeleteUserModal,
  deleteUser as deleteUserModal,
  handleUserAction,
} from './modals';

// =====================================================================
// 1. Utility layer (Phase 2)
// =====================================================================
// Publish each utility as a window.* global. We only set if missing —
// this avoids clobbering any other bundle that may have provided its
// own version. In Phase 9 we'll delete these once the inline <script>
// blocks are gone.

interface WindowWithUtils extends Window {
  escapeHtml?: typeof escapeHtml;
  splitCurrency?: typeof splitCurrency;
  buildCurrencyLookup?: typeof buildCurrencyLookup;
  getCurrencyByISO?: typeof getCurrencyByISO;
  formatCurrency?: typeof formatCurrency;
  formatCurrencyByISO?: typeof formatCurrencyByISO;
  getTotalByCurrency?: typeof getTotalByCurrency;
  formatTs?: typeof formatTs;
  formatDuration?: typeof formatDuration;
  driftSrv2Local?: typeof driftSrv2Local;
  lastSeenFor?: typeof lastSeenFor;
  displayTerminalName?: typeof displayTerminalName;
  displayTerminalLocation?: typeof displayTerminalLocation;
  displayTerminalID?: typeof displayTerminalID;
  displayTerminalIcon?: typeof displayTerminalIcon;
  quickStatusIcons?: typeof quickStatusIcons;
  logKey?: typeof logKey;
  terminalLogRow?: typeof terminalLogRow;
  apiFetchCore?: typeof apiFetch;
  renderKPIs?: typeof renderKPIs;
  renderQuickTerminals?: typeof renderQuickTerminals;
  renderAllTerminals?: typeof renderAllTerminals;
  renderGlobalLogs?: typeof renderGlobalLogs;
  renderTerminalLogs?: typeof renderTerminalLogs;
  renderGroups?: typeof renderGroups;
  renderUsers?: typeof renderUsers;
  renderLoginAttempts?: typeof renderLoginAttempts;
  renderNotify?: typeof renderNotify;
  installNotifyClickHandler?: typeof installNotifyClickHandler;
  // Incremental DOM update used by markNotifyRead so removing
  // one row doesn't wipe the whole notify panel (which would
  // collapse every <details> bucket the user had expanded).
  removeNotifyRow?: typeof removeNotifyRow;
  renderAll?: typeof renderAll;
  groupTerminalsByOid?: typeof groupTerminalsByOid;
  // Group rename — exposed via window.editGroupName so the inline
  // onclick on the renderGroups card title can call it.
  editGroupName?: typeof editGroupName;
  fetchDataBundle?: typeof fetchDataBundle;
  fetchTerminalBundle?: typeof fetchTerminalBundle;
  fetchGroupsBundle?: typeof fetchGroupsBundle;
  fetchUsersBundle?: typeof fetchUsersBundle;
  fetchLoginAttemptsBundle?: typeof fetchLoginAttemptsBundle;
  fetchNotify?: typeof fetchNotifyBundle;
  markNotifyRead?: typeof markNotifyReadBundle;
  // Phase 12.16: per-ISO currency defaults loader. Exposed at
  // window.loadCurrencyDefaults so the inline window.onload
  // forwarder in admin/index.php can call it on the page-reload
  // path (existing session). The doLogin path calls it directly
  // (see auth.ts) so it doesn't go through the inline forwarder.
  loadCurrencyDefaults?: typeof loadCurrencyDefaultsBundle;
  // Phase 12.11: bundle-owned log fetch. Exposed at window.fetchLogs
  // (no "Bundle" suffix) so the inline startLogPolling and the bundle's
  // navigation.ts lazy-load both call the same symbol.
  fetchLogs?: typeof fetchLogsBundle;
  addGroupModal?: typeof addGroupModal;
  closeAddGroupModal?: typeof closeAddGroupModal;
  submitAddGroupModal?: typeof submitAddGroupModal;
  addUserModal?: typeof addUserModal;
  closeAddUserModal?: typeof closeAddUserModal;
  toggleAddUserGroupField?: typeof toggleAddUserGroupField;
  submitAddUserModal?: typeof submitAddUserModal;
  showChangePasswordModal?: typeof showChangePasswordModal;
  changeUserPassword?: typeof changeUserPassword;
  showDeleteUserModal?: typeof showDeleteUserModal;
  deleteUserModal?: typeof deleteUserModal;
  handleUserAction?: typeof handleUserAction;
  doLogin?: typeof doLogin;
  doLogout?: typeof doLogout;
  showLoginModal?: typeof showLoginModal;
  hideLoginModal?: typeof hideLoginModal;
  isOperator?: typeof isOperator;
  applyRoleGating?: typeof applyRoleGating;
  ensureLoginErrorPlacement?: typeof ensureLoginErrorPlacement;
  clearLoginErrors?: typeof clearLoginErrors;
  // Phase 9: terminal modal + accounting + navigation + IP
  openTerminalImpl?: typeof openTerminalImpl;
  closeModal?: typeof closeModal;
  switchModalTab?: typeof switchModalTab;
  saveSettings?: typeof saveSettings;
  editTerminalField?: typeof editTerminalField;
  showConfirm?: typeof showConfirm;
  setPendingSetting?: typeof setPendingSetting;
  setPendingPrice?: typeof setPendingPrice;
  setPendingBetValue?: typeof setPendingBetValue;
  setRuntime?: typeof setRuntime;
  toggleStatus?: typeof toggleStatus;
  startHopperRefresh?: typeof startHopperRefresh;
  stopHopperRefresh?: typeof stopHopperRefresh;
  updateHopperLevelsDisplay?: typeof updateHopperLevelsDisplay;
  prepareTerminal?: typeof prepareTerminal;
  renderCurrencyTotals?: typeof renderCurrencyTotals;
  hasOwn?: typeof hasOwn;
  normaliseAccounting?: typeof normaliseAccounting;
  pickByIndex?: typeof pickByIndex;
  renderAccounting?: typeof renderAccounting;
  renderAccTable?: typeof renderAccTable;
  renderAccountingRanged?: typeof renderAccountingRanged;
  clearRangedState?: typeof clearRangedState;
  // Phase 12.12: group-level Accounting page.
  renderAccountingGroup?: typeof renderAccountingGroup;
  clearGroupAccountingState?: typeof clearGroupAccountingState;
  navigate?: typeof navigate;
  toggleMobileMenu?: typeof toggleMobileMenu;
  resolveAndShowIp?: typeof resolveAndShowIp;
  expandIpDetails?: typeof expandIpDetails;
  clearIpGeoCache?: typeof clearIpGeoCache;
  initTailwind?: typeof initTailwind;
  showSpinner?: typeof showSpinner;
  hideSpinner?: typeof hideSpinner;
  setSpinnerMessage?: typeof setSpinnerMessage;
  isSpinnerVisible?: typeof isSpinnerVisible;
  renderTab?: typeof renderTab;
  renderTabOverview?: typeof renderTabOverview;
  renderTabLogs?: typeof renderTabLogs;
  renderTabSettings?: typeof renderTabSettings;
  renderTabCommands?: typeof renderTabCommands;
  renderTabAccounting?: typeof renderTabAccounting;
  moveTerminal?: typeof moveTerminal;
  // Phase 12.10: user-badge dropdown + Profile / Settings modals
  toggleUserMenu?: typeof toggleUserMenu;
  openUserMenu?: typeof openUserMenu;
  closeUserMenu?: typeof closeUserMenu;
  handleUserMenuClick?: typeof handleUserMenuClick;
  openProfileModal?: typeof openProfileModal;
  closeProfileModal?: typeof closeProfileModal;
  submitChangeMyPassword?: typeof submitChangeMyPassword;
  openSettingsModal?: typeof openSettingsModal;
  closeSettingsModal?: typeof closeSettingsModal;
  // Phase 12.18: programmatic language setter (reuses the same
  // /api/change_my_lang path the Settings modal click handler uses).
  setLanguage?: typeof setLanguage;
  // Phase 12.18: i18n bridges for out-of-bundle callers
  tBundle?: typeof tBundle;
  activeLangBundle?: typeof activeLangBundle;
  setActiveLangBundle?: (l: LangBundle) => void;
  // Phase 14: Web Push (subscribe / unsubscribe / status).
  ensurePushReady?: typeof ensurePushReady;
  subscribeToPush?: typeof subscribeToPush;
  unsubscribeFromPush?: typeof unsubscribeFromPush;
  logoutUnsubscribePush?: typeof logoutUnsubscribePush;
  attachPushState?: typeof attachPushState;
  __timestop_vite_loaded?: boolean;
}

const w = window as WindowWithUtils;

const utilsExports: Record<string, unknown> = {
  escapeHtml,
  splitCurrency,
  buildCurrencyLookup,
  getCurrencyByISO,
  formatCurrency,
  formatCurrencyByISO,
  getTotalByCurrency,
  formatTs,
  formatDuration,
  driftSrv2Local,
  lastSeenFor,
  displayTerminalName,
  displayTerminalLocation,
  displayTerminalID,
  displayTerminalIcon,
  quickStatusIcons,
  logKey,
  terminalLogRow,
};

for (const [name, value] of Object.entries(utilsExports)) {
  if (typeof w[name as keyof WindowWithUtils] === 'undefined') {
    (w as unknown as Record<string, unknown>)[name] = value;
  }
}

// =====================================================================
// 2. API layer (Phase 3)
// =====================================================================
// The bundle's apiFetch lives at window.apiFetchCore. The thin wrapper
// in admin/index.php stays in charge of:
//   - reading csrfToken (a module-scope let in the inline scripts)
//   - calling window.apiFetchCore() with that token
//   - calling showLoginModal() on 401 (the inline-script's responsibility
//     until Phase 6 moves auth into the bundle)
//
// This split lets the API module stay pure (one input, one output) while
// the legacy side effects stay where they were.

if (typeof w.apiFetchCore === 'undefined') {
  w.apiFetchCore = apiFetch;
}

// =====================================================================
// 2b. State container (Phase 4)
// =====================================================================
// Initialise window.timestop.state with the empty defaults. The inline
// scripts in admin/index.php read currentUser / csrfToken from this
// container via `let X = window.timestop.state.X`, so the order matters:
// this MUST run before any inline script reads the state. Because the
// inline bootstrap script in index.php also initialises window.timestop,
// we use attachState() which only sets state if it isn't already set.
//
// We do NOT import currentUser / csrfToken from PHP here — the inline
// scripts handle that part because they're parsed in the same HTML
// response. The bundle just provides the container shape.

attachState(createInitialState(null, null));

// =====================================================================
// 3. Render layer (Phase 7: force-overwrite)
// =====================================================================
// The render functions live in the bundle and are the authoritative
// implementation. Earlier phases used idempotent `if (typeof w.X ===
// 'undefined')` guards, but the inline <script> blocks in index.php
// declare matching function names during HTML parse, which become
// window.* globals before this deferred module runs. The bundle's
// guards then saw the inline versions and skipped the install — so
// the inline renderTerminalLogs (and friends) ran in production while
// the bundle versions were dead code.
//
// Phase 7 flips the guards to unconditional assignment. The inline
// function declarations remain on disk as dead code for now; Phase 9
// deletes them entirely. The console.asserts at the bottom of this
// block will fire loudly if a future regression re-introduces the
// shadow problem.
//
// groupTerminalsByOid / fetchDataBundle / fetchGroupsBundle /
// fetchUsersBundle still use idempotent guards because they have no
// inline counterpart — they were always bundle-only.

w.renderKPIs            = renderKPIs;
w.renderQuickTerminals  = renderQuickTerminals;
w.renderAllTerminals    = renderAllTerminals;
w.renderGlobalLogs      = renderGlobalLogs;
w.renderTerminalLogs    = renderTerminalLogs;
w.renderGroups          = renderGroups;
w.renderUsers           = renderUsers;
w.renderLoginAttempts   = renderLoginAttempts;
w.renderNotify          = renderNotify;
w.removeNotifyRow       = removeNotifyRow;
w.renderAll             = renderAll;
w.editGroupName          = editGroupName;

if (typeof w.groupTerminalsByOid === 'undefined')   w.groupTerminalsByOid = groupTerminalsByOid;
if (typeof w.fetchDataBundle === 'undefined')       w.fetchDataBundle = fetchDataBundle;
if (typeof w.fetchTerminalBundle === 'undefined')   w.fetchTerminalBundle = fetchTerminalBundle;
if (typeof w.fetchGroupsBundle === 'undefined')     w.fetchGroupsBundle = fetchGroupsBundle;
if (typeof w.fetchUsersBundle === 'undefined')      w.fetchUsersBundle = fetchUsersBundle;
if (typeof w.fetchLoginAttemptsBundle === 'undefined') w.fetchLoginAttemptsBundle = fetchLoginAttemptsBundle;
if (typeof w.loadCurrencyDefaults === 'undefined')  w.loadCurrencyDefaults = loadCurrencyDefaultsBundle;
// Phase 12.11: bundle owns the only log fetch path. Inline
// scripts (startLogPolling, navigate(3) lazy-load) call
// window.fetchLogs(state, config, opts).
if (typeof w.fetchLogs === 'undefined')             w.fetchLogs = fetchLogsBundle;
// Phase 12.17: notify feed loader + mark-as-read helper. fetchNotify
// is a bundle-only symbol (no inline counterpart); markNotifyRead is
// the same. The HTML <button> in the dashboard notify panel uses
// onclick="notifyReadClick(id)" (see main.ts adapter below), which
// reads window.timestop.state and forwards here.
if (typeof w.fetchNotify === 'undefined')           w.fetchNotify = fetchNotifyBundle;
if (typeof w.markNotifyRead === 'undefined')        w.markNotifyRead = markNotifyReadBundle;

// One-shot bundle-load assert. Fires ONCE per page load (we're at
// module init time, so this runs exactly once). If any render fn on
// window.* is still pointing at the inline version, the assert will
// trip — loud, immediate, debuggable.
{
  const checks: Array<[string, unknown]> = [
    ['renderKPIs',           renderKPIs],
    ['renderQuickTerminals', renderQuickTerminals],
    ['renderAllTerminals',   renderAllTerminals],
    ['renderGlobalLogs',     renderGlobalLogs],
    ['renderTerminalLogs',   renderTerminalLogs],
    ['renderGroups',         renderGroups],
    ['renderUsers',          renderUsers],
    ['renderLoginAttempts',  renderLoginAttempts],
    ['renderNotify',         renderNotify],
    ['renderAll',            renderAll],
  ];
  for (const [name, bundleRef] of checks) {
    const winRef = (w as unknown as Record<string, unknown>)[name];
    if (winRef !== bundleRef) {
      console.error('[timestop] render fn ' + name + ' on window is NOT the bundle version — inline shadow present?');
    }
  }
}

// =====================================================================
// 4. Modal layer (Phase 5)
// =====================================================================
// Expose modal handlers. The inline scripts' addGroup/addUser/etc still
// drive the page during the migration window; Phase 9 swaps them.

if (typeof w.addGroupModal === 'undefined')            w.addGroupModal = addGroupModal;
if (typeof w.closeAddGroupModal === 'undefined')       w.closeAddGroupModal = closeAddGroupModal;
if (typeof w.submitAddGroupModal === 'undefined')      w.submitAddGroupModal = submitAddGroupModal;
if (typeof w.addUserModal === 'undefined')             w.addUserModal = addUserModal;
if (typeof w.closeAddUserModal === 'undefined')        w.closeAddUserModal = closeAddUserModal;
if (typeof w.toggleAddUserGroupField === 'undefined')  w.toggleAddUserGroupField = toggleAddUserGroupField;
if (typeof w.submitAddUserModal === 'undefined')       w.submitAddUserModal = submitAddUserModal;
if (typeof w.showChangePasswordModal === 'undefined')  w.showChangePasswordModal = showChangePasswordModal;
if (typeof w.changeUserPassword === 'undefined')       w.changeUserPassword = changeUserPassword;
if (typeof w.showDeleteUserModal === 'undefined')      w.showDeleteUserModal = showDeleteUserModal;
if (typeof w.deleteUserModal === 'undefined')          w.deleteUserModal = deleteUserModal;
if (typeof w.handleUserAction === 'undefined')         w.handleUserAction = handleUserAction;

// =====================================================================
// 5. Auth layer (Phase 6a)
// =====================================================================
// Expose auth + role-gating helpers. The inline <script> keeps thin
// const-arrow wrappers for any HTML onclick (doLogin / doLogout / etc.)
// that still reference these names — the wrapper reads window.timestop.state
// + window.apiFetchCore's config and forwards here.

if (typeof w.doLogin === 'undefined')         w.doLogin = doLogin;
if (typeof w.doLogout === 'undefined')       w.doLogout = doLogout;
if (typeof w.showLoginModal === 'undefined') w.showLoginModal = showLoginModal;
if (typeof w.hideLoginModal === 'undefined') w.hideLoginModal = hideLoginModal;
if (typeof w.isOperator === 'undefined')     w.isOperator = isOperator;
if (typeof w.applyRoleGating === 'undefined') w.applyRoleGating = applyRoleGating;
if (typeof w.ensureLoginErrorPlacement === 'undefined') w.ensureLoginErrorPlacement = ensureLoginErrorPlacement;
if (typeof w.clearLoginErrors === 'undefined')           w.clearLoginErrors = clearLoginErrors;

// =====================================================================
// 6. Terminal modal + accounting + navigation + IP (Phase 9)
// =====================================================================
w.openTerminalImpl = openTerminalImpl;
w.closeModal = closeModal;
w.switchModalTab = switchModalTab;
w.saveSettings = saveSettings;
w.editTerminalField = editTerminalField;
w.showConfirm = showConfirm;
w.setPendingSetting = setPendingSetting;
w.toggleStatus = toggleStatus;
w.startHopperRefresh = startHopperRefresh;
w.stopHopperRefresh = stopHopperRefresh;
w.updateHopperLevelsDisplay = updateHopperLevelsDisplay;
w.prepareTerminal = prepareTerminal;
w.renderCurrencyTotals = renderCurrencyTotals;
w.hasOwn = hasOwn;
w.normaliseAccounting = normaliseAccounting;
w.pickByIndex = pickByIndex;
w.renderAccounting = renderAccounting;
w.renderAccTable = renderAccTable;
w.renderAccountingRanged = renderAccountingRanged;
w.clearRangedState = clearRangedState;
w.renderAccountingGroup = renderAccountingGroup;
w.clearGroupAccountingState = clearGroupAccountingState;
w.navigate = navigate;
w.toggleMobileMenu = toggleMobileMenu;
w.resolveAndShowIp = resolveAndShowIp;
w.expandIpDetails = expandIpDetails;
w.clearIpGeoCache = clearIpGeoCache;
w.initTailwind = initTailwind;
w.showSpinner = showSpinner;
w.hideSpinner = hideSpinner;
w.setSpinnerMessage = setSpinnerMessage;
w.isSpinnerVisible = isSpinnerVisible;
w.renderTab = renderTab;
w.renderTabOverview = renderTabOverview;
w.renderTabLogs = renderTabLogs;
w.renderTabSettings = renderTabSettings;
w.renderTabCommands = renderTabCommands;
w.renderTabAccounting = renderTabAccounting;
w.moveTerminal = moveTerminal;
w.setPendingPrice = setPendingPrice;
w.setPendingBetValue = setPendingBetValue;
w.setRuntime = setRuntime;

// Phase 12.10: user-menu (HTML onclick adapters need these on window)
w.toggleUserMenu = toggleUserMenu;
w.openUserMenu = openUserMenu;
w.closeUserMenu = closeUserMenu;
w.handleUserMenuClick = handleUserMenuClick;
w.openProfileModal = openProfileModal;
w.closeProfileModal = closeProfileModal;
w.submitChangeMyPassword = submitChangeMyPassword;
w.openSettingsModal = openSettingsModal;
w.closeSettingsModal = closeSettingsModal;
// Phase 12.18: programmatic language setter (reuses the same
// /api/change_my_lang path the Settings modal click handler uses).
w.setLanguage = setLanguage;
// Phase 12.18: t() exposed at window.* for inline scripts and the
// ad-hoc renderers we don't yet migrate. The helper reads from the
// same admin/locales/*.json files the PHP-side t() reads.
w.tBundle = tBundle;
w.activeLangBundle = activeLangBundle;
// Phase 12.18: setActiveLang bridge for out-of-bundle callers
// (e.g. user-menu.ts's setLanguage, which can't take a direct
// import of i18n.ts without a circular dep with main.ts).
w.setActiveLangBundle = (l: LangBundle) => setActiveLang(l);

// Phase 12.13: avatar picker + cropper + badge painter
w.openAvatarPicker = openAvatarPicker;
w.closeAvatarCropper = closeAvatarCropper;
w.confirmAvatarCrop = confirmAvatarCrop;
w.paintUserBadge = paintUserBadge;

// Phase 14: Web Push.
w.ensurePushReady = ensurePushReady;
w.subscribeToPush = subscribeToPush;
w.unsubscribeFromPush = unsubscribeFromPush;
w.logoutUnsubscribePush = logoutUnsubscribePush;
w.attachPushState = attachPushState;

// =====================================================================
// 6b. i18n re-render wiring (Phase 12.18)
// =====================================================================
// After a language change, the active page and any open modal need
// to be re-painted so the next render call shows the new strings.
// The re-render trigger is a single onLangChange() subscriber that
// calls navigate(activePage) or renderTab(activeTab) on each
// langChange event. We track the active page and tab by wrapping
// the existing window.navigate and window.renderTab shims; the
// original closeModal() resets our "modal is open" flag.
let _activePage: number = 0;
let _activeTab: number = -1;
let _modalOpen: boolean = false;

{
    // Wrap window.navigate so we can re-paint the active page on a
    // language change. The bundle's signature is
    //   navigate(stateOrPage: TimestopState | number, page?: number): void
    // and TWO call sites use it:
    //   - HTML onclick="navigate(0)" — bare numeric, one arg
    //   - inline index.php `navigate(page)` wrapper that calls
    //     window.navigate(state, page) — two args, state first
    // The previous wrap took only `(n: number)`, which silently
    // dropped the `page` arg when the inline style called us with
    // `(state, page)`, leaving the visible page stuck at 0. Forward
    // the full signature.
    const _navigate = w.navigate;
    w.navigate = ((stateOrPage: TimestopState | number, page?: number): void => {
        // Track the active page for the i18n re-paint. Both call
        // styles land in a number for the page; the bundle-style
        // call has the number as the SECOND argument.
        const p = (typeof stateOrPage === 'number') ? stateOrPage : page;
        if (typeof p === 'number') _activePage = p;
        if (typeof _navigate === 'function') _navigate(stateOrPage, page);
    }) as typeof navigate;
    const _renderTab = w.renderTab;
    w.renderTab = (n: number) => {
        _activeTab = n;
        _modalOpen = true;
        if (typeof _renderTab === 'function') _renderTab(n);
    };
}

onLangChange(() => {
    try {
        if (_modalOpen && _activeTab >= 0 && typeof w.renderTab === 'function') {
            w.renderTab(_activeTab);
        } else if (typeof w.navigate === 'function' && _activePage >= 0) {
            w.navigate(_activePage);
        }
        // The Ranged sub-tab holds a per-terminal acc_diff cache;
        // its event strings were already rendered into the DOM via
        // t() and won't re-derive until the cache is wiped. Same
        // for the group-level accounting page.
        if (typeof w.clearRangedState === 'function') w.clearRangedState();
        if (typeof w.clearGroupAccountingState === 'function') w.clearGroupAccountingState();
    } catch (e) {
        console.warn('[timestop] onLangChange repaint failed:', e);
    }
});

// =====================================================================

// =====================================================================
// 7. HTML onclick adapters (Phase 9)
// =====================================================================
// The bundle functions take state: TimestopState as first arg. HTML onclick
// attributes call them without state (e.g. closeModal(), saveSettings()).
// These adapters detect the HTML onclick signature and inject state from
// window.timestop.state. The bundle's real impl is captured before overwrite.
{
  const w2 = w as unknown as Record<string, unknown>;
  const st = () => (window as unknown as { timestop: { state: TimestopState } }).timestop.state;
  const cfg = (): ApiFetchConfig => ({ apiBase: '/api/', csrfToken: st().csrfToken });

  const _closeModal = w2.closeModal as (s: TimestopState) => void;
  w2.closeModal = () => { _modalOpen = false; _activeTab = -1; if (_closeModal) _closeModal(st()); };

  const _saveSettings = w2.saveSettings as (s: TimestopState, c: ApiFetchConfig, fn: typeof apiFetch) => unknown;
  w2.saveSettings = () => { if (_saveSettings) _saveSettings(st(), cfg(), apiFetch); };

  const _setPendingSetting = w2.setPendingSetting as ( k: string, v: unknown,s: TimestopState) => void;
  w2.setPendingSetting = (key: string, value: unknown) => { if (_setPendingSetting) _setPendingSetting( key, value,st()); };

  const _setPendingPrice = w2.setPendingPrice as (h: number, idx: number, v: string, s: TimestopState) => void;
  w2.setPendingPrice = (h: number, idx: number, v: string) => { if (_setPendingPrice) _setPendingPrice(h, idx, v, st()); };

  // Bets inputs: onchange="setPendingBetValue('p', 1, this.value)" --
  // the adapter injects the live state, same pattern as setPendingPrice.
  const _setPendingBetValue = w2.setPendingBetValue as (f: 'p' | 't', b: number, v: string, s: TimestopState) => void;
  w2.setPendingBetValue = (f: 'p' | 't', b: number, v: string) => { if (_setPendingBetValue) _setPendingBetValue(f, b, v, st()); };

  // setRuntime -- the inline onclick in the Settings tab calls
  // setRuntime(value); the function reads state from
  // window.timestop.state via liveState() and uses the imported
  // apiFetch singleton, so the bridge just exposes the bundle's
  // own function on window. setRuntime is async (POST round-
  // trip); the adapter returns the unresolved Promise so the
  // inline onclick can ignore the result. The optimistic UI
  // inside setRuntime paints the new border immediately.
  w2.setRuntime = setRuntime;

  const _toggleStatus = w2.toggleStatus as (s: TimestopState) => void;
  w2.toggleStatus = () => { if (_toggleStatus) _toggleStatus(st()); };

  const _editTerminalField = w2.editTerminalField as (s: TimestopState, f: string, el: HTMLElement, c: ApiFetchConfig, fn: typeof apiFetch) => Promise<void>;
  w2.editTerminalField = (field: string, el: HTMLElement) => { if (_editTerminalField) void _editTerminalField(st(), field, el, cfg(), apiFetch); };

  const _addGroup = w2.addGroupModal as (s: TimestopState) => void;
  w2.addGroup = () => { if (_addGroup) _addGroup(st()); };

  const _addUser = w2.addUserModal as (s: TimestopState) => void;
  w2.addUser = () => { if (_addUser) _addUser(st()); };

  const _submitAddGroup = w2.submitAddGroupModal as (ev: Event, s: TimestopState, c: ApiFetchConfig) => Promise<void>;
  w2.submitAddGroup = (ev: Event) => { ev.preventDefault(); if (_submitAddGroup) void _submitAddGroup(ev, st(), cfg()); };

  const _submitAddUser = w2.submitAddUserModal as (ev: Event, s: TimestopState, c: ApiFetchConfig) => Promise<void>;
  w2.submitAddUser = (ev: Event) => { ev.preventDefault(); if (_submitAddUser) void _submitAddUser(ev, st(), cfg()); };

  // Phase 12.10: user-badge menu + Profile / Settings
  const _handleUserMenuClick = w2.handleUserMenuClick as (a: string, s: TimestopState, c: ApiFetchConfig) => void;
  w2.handleUserMenuClick = (action: string) => { if (_handleUserMenuClick) _handleUserMenuClick(action, st(), cfg()); };

  const _submitChangeMyPassword = w2.submitChangeMyPassword as (ev: Event, s: TimestopState, c: ApiFetchConfig) => Promise<boolean>;
  w2.submitChangeMyPassword = (ev: Event) => { if (_submitChangeMyPassword) void _submitChangeMyPassword(ev, st(), cfg()); return false; };

  // Phase 12.17: notify panel row clicks go through the delegated
  // listener installed by installNotifyClickHandler() at module
  // init; the listener reads data-notify-id off the row and calls
  // window.markNotifyRead directly, so no per-call adapter is
  // needed here. fetchNotify is re-exposed at window.fetchNotify so
  // the inline window.onload forwarder (page-reload path) can call
  // it the same way it calls loadCurrencyDefaults.
  const _fetchNotify = w2.fetchNotify as (s: TimestopState, c: ApiFetchConfig) => Promise<unknown[]>;
  w2.fetchNotify = () => { if (_fetchNotify) return _fetchNotify(st(), cfg()); return Promise.resolve([]); };
}

// 3. Boot diagnostics
// =====================================================================

w.__timestop_vite_loaded = true;

// Phase 12.18: kick off the locale catalog load. We don't await it
// here -- the page already has the PHP-side t() chrome (Stage 1
// wired this in admin/shell.php + admin/index.php), and the bundle
// renderers repaint in their next call after setActiveLang() fires.
// The first paint may briefly show EN strings (the original JS
// literals) before the catalog arrives; the re-paint flips them
// to the user's chosen language in <100ms. An over-eager
// "don't show until ready" gate would feel slower than that flash.
void (async () => {
    try {
        await loadLocales();
        const st = (window as unknown as {
            timestop: { state: { currentUser?: { lang?: string } | null } };
        }).timestop.state;
        const seed = (st.currentUser && typeof st.currentUser.lang === 'string'
                      && (st.currentUser.lang === 'en' || st.currentUser.lang === 'cz' || st.currentUser.lang === 'pl'))
            ? (st.currentUser.lang as LangBundle)
            : 'en';
        setActiveLang(seed);
    } catch (e) {
        console.warn('[timestop] i18n boot failed:', e);
    }
})();

// Phase 12.17: install the notify-panel click handler. Idempotent
// (the function no-ops on repeat calls), so calling it here at
// module init covers every page-load path: the bundle's own
// first-paint, the page-reload path (existing session), and any
// future Vite HMR reload. We don't need to wait for DOMContentLoaded
// because the handler is a document-level delegated listener that
// fires on bubbles from #dashboard-notify-body, which only exists
// once the shell has loaded — by which time the listener is
// already installed.
installNotifyClickHandler();


if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    // Phase 12.2: position #login-error AFTER the Sign-in button. The
    // canonical markup in admin/index.php is also being updated to
    // match, but admin/index.php doesn't auto-deploy on this PC, so
    // the JS-side move ships via the Vite tunnel. Idempotent.
    ensureLoginErrorPlacement();

    // const banner = document.createElement('div');
    // banner.id = 'vite-scaffold-banner';
    // banner.textContent = 'Vite bundle active (utils + api + render + modals)';
    // banner.style.cssText = 'position:fixed;bottom:8px;left:8px;z-index:9999;background:#10b981;color:white;font:12px sans-serif;padding:4px 10px;border-radius:12px;opacity:0.85;pointer-events:none;';
    // document.body.appendChild(banner);

    // Sanity-check the utility round-trip — if this fails we know our
    // module wiring is broken before any user-visible regression.
    // const test = escapeHtml('<script>alert(1)</script>');
    // if (test !== '&lt;script&gt;alert(1)&lt;/script&gt;') {
    //   console.error('[timestop] escapeHtml self-test FAILED:', test);
    // } else {
    //   console.log('[timestop] escapeHtml self-test OK');
    // }

    // Sanity-check the API client is wired up (call it with a deliberately
    // bad endpoint and confirm we get a structured envelope back).
    // void (async () => {
    //   try {
    //     const env = await apiFetch('__timestop_self_test', {}, { apiBase: '/api/', csrfToken: null });
    //     if (env && typeof env.ok === 'boolean' && typeof env.status === 'number') {
    //       console.log('[timestop] apiFetch self-test OK (status=' + env.status + ')');
    //     } else {
    //       console.error('[timestop] apiFetch self-test FAILED:', env);
    //     }
    //   } catch (e) {
    //     console.error('[timestop] apiFetch self-test threw:', e);
    //   }
    // })();
  });
}

// Re-export types so consumers can pull them through this entry if they
// want a single import path during the migration window.
export type { ApiFetchOptions, ApiFetchConfig };